import { useTranslation } from 'react-i18next';
import { useStudyAnalytics } from '../../hooks/useStudyAnalytics';
import StatCard from '../StatCard';
import {
  ScoreTrendChart,
  TopicRadarChart,
  WeakTopicsPanel,
  DifficultyBreakdown,
  TimeAnalyticsPanel,
} from '../dashboard';
import QuestionsOverTimeChart from './QuestionsOverTimeChart';
import { Card } from '../ui';

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
}

function Section({
  title,
  children,
  className = '',
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card variant="subtle" className={`p-6 ${className}`}>
      <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">{title}</h2>
      {children}
    </Card>
  );
}

export default function StudyAnalytics() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useStudyAnalytics();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent dark:border-blue-400 dark:border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400">
        {t('dashboard.study.analytics.errorLoad')}
      </div>
    );
  }

  if (!data || data.summary.total_answered === 0) {
    return (
      <Card variant="subtle" className="flex flex-col items-center py-16 text-center">
        <p className="text-lg text-slate-500 dark:text-slate-400">{t('dashboard.study.analytics.emptyTitle')}</p>
        <p className="mt-1 text-sm text-slate-400 dark:text-slate-500">
          {t('dashboard.study.analytics.emptyDescription')}
        </p>
      </Card>
    );
  }

  const s = data.summary;

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t('dashboard.stats.studySessions')} value={s.total_sessions} icon="study-sessions" />
        <StatCard label={t('dashboard.stats.questionsAnswered')} value={s.total_answered} icon="questions" />
        <StatCard
          label={t('dashboard.stats.studyAccuracy')}
          value={`${Math.round(s.accuracy_percent)}%`}
          icon="accuracy"
        />
        <StatCard
          label={t('dashboard.stats.avgDuration')}
          value={formatDuration(s.avg_session_duration_seconds)}
          icon="duration"
        />
      </div>

      {/* Score Trend + Questions Over Time */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title={t('dashboard.study.analytics.scoreTrend')}>
          <ScoreTrendChart data={data.score_trend} />
        </Section>
        <Section title={t('dashboard.study.analytics.questionsOverTime')}>
          <QuestionsOverTimeChart data={data.questions_over_time} />
        </Section>
      </div>

      {/* Topic Mastery + Weak Topics */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title={t('dashboard.study.analytics.topicMastery')}>
          <TopicRadarChart data={data.topic_mastery} />
        </Section>
        <Section title={t('dashboard.study.analytics.weakTopics')}>
          <WeakTopicsPanel topics={data.weak_topics} />
        </Section>
      </div>

      {/* Difficulty + Time Analytics */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title={t('dashboard.study.analytics.byDifficulty')}>
          <DifficultyBreakdown data={data.difficulty_breakdown} />
        </Section>
        <Section title={t('dashboard.study.analytics.timeAnalytics')}>
          <TimeAnalyticsPanel data={data.time_analytics} />
        </Section>
      </div>
    </div>
  );
}
